import React, { useState, useEffect, useCallback, useRef } from 'react';
import { View, Text, StyleSheet, Pressable, RefreshControl, Platform } from 'react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { useTranslation } from 'react-i18next';
import { Ionicons } from '@expo/vector-icons';
import { FlashList } from '@shopify/flash-list';
import { RootStackParamList, Conversation } from '../types';
import { AppHeader, ScreenLayout, ClubMark, PageBand } from '../components';
import { useResponsive } from '../utils/responsive';
import { Colors, Fonts, Radii, Spacing, Typography, LAYOUT } from '../constants';
import { supabase } from '../config/supabase';
import { conversationsService } from '../services/conversations';
import { authService } from '../services/auth';
import { imageService } from '../services/images';
import { useDialog } from '../contexts';
import { Image } from 'expo-image';

type Props = NativeStackScreenProps<RootStackParamList, 'ConversationsList'>;

interface ConversationWithUnread extends Conversation {
  unreadCount?: number;
  lastMessagePreview?: string;
  lastMessageTime?: string;
  lastMessageType?: string;
  lastMessageMetadata?: Record<string, any> | null;
}

export const ConversationsListScreen: React.FC<Props> = ({ navigation }) => {
  const { t } = useTranslation();
  const dialog = useDialog();
  const { isDesktop } = useResponsive();
  const [conversations, setConversations] = useState<ConversationWithUnread[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  // Incremented per load; an out-of-order response (slow older reload racing a
  // newer realtime-triggered one) is discarded instead of overwriting the list.
  const loadIdRef = useRef(0);

  useEffect(() => {
    loadCurrentUser();
  }, []);

  // (Re)load whenever the screen gains focus — the initial 'focus' event
  // covers the first load, and returning from a chat refreshes previews,
  // unread counts and ordering.
  useEffect(() => {
    const unsubscribe = navigation.addListener('focus', () => {
      loadConversations();
    });
    return unsubscribe;
  }, [navigation]);

  // Live refresh: reload the list when any message changes, so an incoming
  // message updates and re-sorts the conversations without a manual refresh.
  // Same channel pattern as UnreadCountContext (the red badge); realtime
  // respects RLS, so only events for the user's own conversations arrive.
  useEffect(() => {
    const channel = supabase
      .channel('conversations-list-refresh')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'messages',
        },
        () => {
          loadConversations();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const loadCurrentUser = async () => {
    const user = await authService.getCurrentUser();
    setCurrentUserId(user?.id || null);
  };

  const loadConversations = async () => {
    const loadId = ++loadIdRef.current;
    try {
      // Use optimized query that fetches conversations with metadata in a single request
      const enrichedConversations = await conversationsService.getConversationsWithMetadata();

      if (loadId !== loadIdRef.current) return;

      // Historical payment-flow system messages render their stored content;
      // only an empty preview gets the "no messages" placeholder.
      const conversationsWithPreviews = enrichedConversations.map((conv) => ({
        ...conv,
        lastMessagePreview: conv.lastMessagePreview || t('screens.conversations.noMessages'),
      }));

      setConversations(conversationsWithPreviews);
    } catch (error: any) {
      dialog.alert(t('common.errors.title'), t('screens.conversations.loadError'), 'error');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const onRefresh = useCallback(() => {
    setRefreshing(true);
    loadConversations();
  }, []);

  const handleConversationPress = (conversation: Conversation) => {
    navigation.navigate('Chat', { conversationId: conversation.id });
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('screens.conversations.justNow');
    if (diffMins < 60) return t('screens.conversations.minutesAgo', { count: diffMins });
    if (diffHours < 24) return t('screens.conversations.hoursAgo', { count: diffHours });
    if (diffDays < 7) return t('screens.conversations.daysAgo', { count: diffDays });

    return date.toLocaleDateString();
  };

  const renderConversationItem = ({ item }: { item: ConversationWithUnread }) => {
    const isBuyer = currentUserId === item.buyer_id;
    const otherUser = isBuyer ? item.seller : item.buyer;
    const unread = item.unreadCount || 0;
    const hasUnread = unread > 0;
    const isItemDeleted = !item.item;

    // Get first image URL (null if item deleted)
    const imageUrl = item.item?.image_urls && item.item.image_urls.length > 0
      ? item.item.image_urls[0]
      : null;

    const otherUserName = otherUser ? `${otherUser.first_name} ${otherUser.last_name}` : '';

    return (
      <Pressable
        style={({ hovered, pressed }: any) => [
          styles.row,
          (hovered || pressed) && styles.rowHovered,
        ]}
        onPress={() => handleConversationPress(item)}
        accessibilityRole="button"
        accessibilityLabel={[otherUserName, item.lastMessagePreview].filter(Boolean).join(': ')}
        testID={`conversation-${item.id}`}
      >
        {/* Item thumbnail + unread count */}
        <View style={styles.thumbWrap}>
          <View style={styles.thumb}>
            {imageUrl ? (
              <Image
                source={{ uri: imageService.getSmallThumbnailUrl(imageUrl) }}
                style={styles.thumbImage}
                contentFit="cover"
              />
            ) : (
              <Ionicons name="image-outline" size={20} color={Colors.border} />
            )}
          </View>
          {hasUnread && (
            <View style={styles.unreadBadge}>
              <Text style={styles.unreadBadgeText}>{unread}</Text>
            </View>
          )}
        </View>

        {/* Conversation details */}
        <View style={styles.content}>
          <View style={styles.topRow}>
            <Text style={styles.name} numberOfLines={1}>
              {otherUserName}
            </Text>
            <Text style={styles.time}>{formatTime(item.lastMessageTime || item.created_at)}</Text>
          </View>

          <Text style={[styles.lastMessage, hasUnread && styles.lastMessageUnread]} numberOfLines={1}>
            {item.lastMessagePreview}
          </Text>

          <View style={styles.metaRow}>
            {item.item?.club ? <ClubMark name={item.item.club.name} size={16} /> : null}
            <Text style={[Typography.meta, styles.metaTitle, isItemDeleted && styles.deletedTitle]} numberOfLines={1}>
              {isItemDeleted ? t('screens.conversations.itemDeleted') : item.item!.title}
            </Text>
            {!isItemDeleted && (
              <>
                <Text style={[Typography.meta, styles.metaDot]}>·</Text>
                <Text style={styles.metaPrice}>€{item.item!.price.toFixed(2)}</Text>
              </>
            )}
            <Text style={styles.role}>
              {isBuyer ? t('screens.conversations.buyer') : t('screens.conversations.seller')}
            </Text>
          </View>
        </View>
      </Pressable>
    );
  };

  // The hairline lives between rows, not on them, so the hover surface can
  // bleed past the content edge with rounded corners and leave the line alone.
  const renderSeparator = () => <View style={styles.separator} />;

  const renderEmptyState = () => (
    <View style={styles.emptyState}>
      <Text style={[Typography.section, styles.emptyStateTitle]}>{t('screens.conversations.noConversations')}</Text>
      <Text style={styles.emptyStateSubtext}>{t('screens.conversations.noConversationsSubtext')}</Text>
    </View>
  );

  // Desktop: the headline lives in the green band. It carries no toolbar, so
  // the column keeps its normal top padding (see `bandToolbar`).
  const band = (
    <PageBand
      title={t('screens.conversations.title')}
      kicker={t('screens.conversations.count', { count: conversations.length })}
    />
  );

  return (
    <ScreenLayout maxWidth={800} mobileTab="ConversationsList" band={band} bandToolbar={false}>
      <View style={styles.container}>
        {!isDesktop && (
          <AppHeader
            navigation={navigation}
            currentScreen="ConversationsList"
            title={t('screens.conversations.title')}
          />
        )}

        <View style={styles.listWrap}>
          {loading ? (
            <View style={styles.centerContainer}>
              <Text>{t('common.loading')}</Text>
            </View>
          ) : (
            <FlashList
              data={conversations}
              renderItem={renderConversationItem}
              keyExtractor={(item) => item.id}
              refreshControl={
                <RefreshControl
                  refreshing={refreshing}
                  onRefresh={onRefresh}
                  colors={[Colors.primary]}
                  tintColor={Colors.primary}
                />
              }
              ListEmptyComponent={renderEmptyState}
              ItemSeparatorComponent={renderSeparator}
              contentContainerStyle={isDesktop ? styles.listContentDesktop : styles.listContentMobile}
            />
          )}
        </View>
      </View>
    </ScreenLayout>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.white,
  },
  listWrap: {
    flex: 1,
    backgroundColor: Colors.white,
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  listContentMobile: {
    paddingHorizontal: Spacing.base,
    paddingTop: 14,
    paddingBottom: LAYOUT.tabBar.contentPadding,
  },
  listContentDesktop: {
    paddingHorizontal: Spacing['2xl'],
    paddingTop: Spacing['2xl'],
    paddingBottom: Spacing['4xl'],
  },

  // Conversation row — a hairline row like the compact ItemCard, no box. The
  // row is pulled out by its own horizontal padding so text and thumbnail stay
  // on the column's content edge while the hover surface gets air around them.
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    paddingVertical: Spacing.md,
    paddingHorizontal: Spacing.md,
    marginHorizontal: -Spacing.md,
    borderRadius: Radii.card,
    ...(Platform.OS === 'web' && { cursor: 'pointer', touchAction: 'pan-y', transitionDuration: '150ms' } as any),
  },
  rowHovered: {
    backgroundColor: Colors.background,
  },
  separator: {
    height: 1,
    backgroundColor: Colors.borderLight,
  },

  // Thumbnail
  thumbWrap: {
    position: 'relative',
    flexShrink: 0,
  },
  thumb: {
    width: 56,
    height: 56,
    borderRadius: Radii.thumb,
    overflow: 'hidden',
    backgroundColor: Colors.card,
    justifyContent: 'center',
    alignItems: 'center',
  },
  thumbImage: {
    width: '100%',
    height: '100%',
  },
  unreadBadge: {
    position: 'absolute',
    top: -6,
    right: -6,
    backgroundColor: Colors.primary,
    borderRadius: 10,
    minWidth: 20,
    height: 20,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 6,
    borderWidth: 2,
    borderColor: Colors.white,
  },
  unreadBadgeText: {
    color: Colors.white,
    fontFamily: Fonts.bold,
    fontSize: 11,
    lineHeight: 14,
  },

  // Content
  content: {
    flex: 1,
    minWidth: 0,
  },
  topRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: Spacing.sm,
  },
  name: {
    fontFamily: Fonts.semiBold,
    fontSize: 15,
    lineHeight: 20,
    color: Colors.text,
    flexShrink: 1,
  },
  time: {
    fontFamily: Fonts.regular,
    fontSize: 12,
    color: Colors.textLight,
    flexShrink: 0,
  },
  lastMessage: {
    fontFamily: Fonts.regular,
    fontSize: 14,
    lineHeight: 20,
    color: Colors.textMedium,
    marginTop: 2,
  },
  lastMessageUnread: {
    fontFamily: Fonts.semiBold,
    color: Colors.text,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 6,
    minWidth: 0,
  },
  metaTitle: {
    color: Colors.textMedium,
    flexShrink: 1,
  },
  deletedTitle: {
    fontStyle: 'italic',
    color: Colors.textLight,
  },
  metaDot: {
    color: Colors.textLight,
  },
  metaPrice: {
    fontFamily: Fonts.bold,
    fontSize: 13,
    color: Colors.textDark,
  },
  role: {
    marginLeft: 'auto',
    fontFamily: Fonts.medium,
    fontSize: 11,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: Colors.textLight,
    flexShrink: 0,
  },

  // Empty state
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: Spacing['2xl'],
    paddingVertical: Spacing['5xl'],
  },
  emptyStateTitle: {
    color: Colors.textDark,
    marginBottom: Spacing.sm,
    textAlign: 'center',
  },
  emptyStateSubtext: {
    fontFamily: Fonts.regular,
    fontSize: 15,
    color: Colors.textMedium,
    textAlign: 'center',
    lineHeight: 22,
  },
});
